import get from 'lodash/get';
import has from 'lodash/has';
import {createEventHandler} from 'recompose';
import fuse from '../../_libs/redux-fusion';
import {getProjectReduxItem} from '../../app/model/reducers/state';
import {FormModalHOC, modalOptions} from '../../components/modals/FormModal';
import {buildCreateRequestModal, buildUpdateRequestModal} from '../../config/redux-list/requestModals';
import {getAsString, getId} from '../../core-utilities/domain/ReduxUtils';
import {getModalSubState} from '../../core-utilities/redux-request-modal/utils';
import {createReduxDropdownList} from '../../core-utilities/redux/ReduxDropdownListContainer';
import {applyDefaultReduxListComponentOptions} from '../../core-utilities/redux/ReduxListContainer';
import {TaskForm} from '../components/TaskForm';
import {TaskType} from '../enums/TaskType';
import {logger} from './logger';


// Calling showModal() must include parent projectId field.
export function makeEditTaskModal(config) {
  const {
    isCreate,
    taskType,
    prefix,
    getState,
    path,
    milestoneReduxList,
  } = config;

  const TaskFormContainer = fuse(StreamedComponent(milestoneReduxList))(TaskForm);
  const EditTaskFormModalComponent = FormModalHOC(TaskFormContainer, {
    modalHeaderLabel: isCreate ? 'tasks.create.header.label' : 'tasks.edit.header.label',
  });

  const buildOnSave = isCreate ? makeOnSave(getState, taskType) : void 0;
  const builderFn = isCreate ? buildCreateRequestModal : buildUpdateRequestModal;

  return builderFn({
    prefix,
    getState,
    path,
    buildOnSave,
    modalOptions: modalOptions,
    modalComponent: EditTaskFormModalComponent,
    logger,
  });
}

function makeOnSave(getState, taskType) {
  return (state$, dispatch, props$, actions) => {
    const {
      handler: onSave,
      stream: save$,
    } = createEventHandler();

    const modalState$ = state$
      .map(getState)
      .map(getModalSubState)
      .distinctUntilChanged()
      .takeUntil(props$.last());

    save$
      .withLatestFrom(modalState$)
      .takeUntil(props$.last())
      .subscribe(([data, modalState]) => {
        const request = taskType === TaskType.ASSET
          ? getAssetTaskRequest(data, modalState)
          : getTaskRequest(data, modalState, taskType);
        dispatch(actions.sendRequest(request));
      });

    return onSave;
  };
}

function getTaskRequest(data, modalState, taskType) {
  const request = {
    ...data,
    isPrivate: !!data.isPrivate,
    milestone: void 0,
    orgId: modalState.orgId,
    projectId: modalState.projectId,
    milestoneId: has(data, 'milestone') ? getId(data.milestone) : getId(get(modalState, 'model.milestone')),
    assetSpecific: taskType !== TaskType.PROJECT,
  };

  if (taskType === TaskType.TEMPLATE) {
    request.templateOptions = {addToExisting: true};
  }

  return request;
}

function getAssetTaskRequest(data, modalState) {
  return {
    ...data,
    isPrivate: !!data.isPrivate,
    milestone: void 0,
    orgId: modalState.orgId,
    projectId: modalState.projectId,
    assetProjectId: modalState.assetProjectId,
    milestoneId: has(data, 'milestone') ? getId(data.milestone) : getId(get(modalState, 'model.milestone')),
  };
}

function StreamedComponent(milestoneReduxList) {
  return (state$, dispatch) => (props$) => {
    const projectId$ = state$
      .map(getProjectReduxItem)
      .map(itemData => itemData.id)
      .distinctUntilChanged()
      .takeUntil(props$.last());

    const propsToList$ = props$
      .combineLatest(
        projectId$,
        (props, projectId) => ({
          ...props,
          projectId,
        }));

    const componentOptions = applyDefaultReduxListComponentOptions(milestoneReduxList.options);

    const {
      dropdownProps$: milestoneProps$,
    } = createReduxDropdownList({
      listProp: 'milestoneProps',
      getParentId: props => props.projectId,
      getLabel: getAsString,
      reduxList: milestoneReduxList,
      options: componentOptions,
      state$,
      dispatch,
      props$: propsToList$,
    });

    return props$
      .combineLatest(
        milestoneProps$,
        (props, milestoneProps) => ({
          ...props,
          ...milestoneProps,
        }));
  };
}



// WEBPACK FOOTER //
// ./src/js/tasks/redux/configEditTaskRequests.js